import { useAuth } from '@/shared/hooks/useAuth';
import { useFeatureFlagState } from '@/shared/hooks/useFeatureFlag';
import type { SettingsSections } from './utils/settingsLinks';

export { settingsLinks } from './utils/settingsLinks';

// The backend's `authorize('admin', 'owner')` on the ad settings routes lets in admin, owner and agency_admin.
const EDIT_ROLES = new Set(['admin', 'owner', 'agency_admin']);

/** Ad settings the practice edits (brand terms, auction insights): owners, admins and agency staff. */
export function useCanEditAdSettings(): boolean {
  const { user } = useAuth();
  return EDIT_ROLES.has(user?.role ?? '');
}

/** Ad settings only agency staff change (the budget override, the change rules): `authorizeAgency` on the backend. */
export function useIsAgencyStaff(): boolean {
  const { user } = useAuth();
  return user?.role === 'agency_admin';
}

/**
 * Which Settings sections a practice sees: only those for modules switched on for it. Operations holds Calls,
 * Insurance Verification and, for text review requests, the practice management system; Communication holds Texting (used by text review requests and patient referrals) and Call
 * Settings (Calls); Organic & Offline's Community sub-section needs Community. `loading` while the practice's flags
 * are still being read, so a page does not flash or redirect too early.
 */
export function useSettingsSections(): SettingsSections {
  const calls = useFeatureFlagState('calls');
  const insurance = useFeatureFlagState('insurance-verification');
  const reviewSms = useFeatureFlagState('review-sms');
  const referrals = useFeatureFlagState('patient-referrals');
  const community = useFeatureFlagState('community');
  const texting = reviewSms.enabled || referrals.enabled;
  return {
    loading: [calls, insurance, reviewSms, referrals, community].some((f) => f.isLoading),
    calls: calls.enabled,
    insurance: insurance.enabled,
    operations: calls.enabled || insurance.enabled || reviewSms.enabled,
    practiceManagement: reviewSms.enabled,
    texting,
    callSettings: calls.enabled,
    communication: texting || calls.enabled,
    community: community.enabled,
  };
}
